import { PerformanceInputRow, PerformanceResultRow } from "../models/calculators.models";
import { PERFORMANCE_ROW_LABELS } from "../utils/calculators.utils";
import CalculatorNumberInput from "./CalculatorNumberInput";

interface PerformanceIndicatorCalculatorProps {
  rows: PerformanceInputRow[];
  results: PerformanceResultRow[];
  onChange: (rows: PerformanceInputRow[]) => void;
  onReset: () => void;
}

type PerformanceNumberField = "expectedSpeed" | "expectedConsumption" | "cpSpeed" | "cpConsumption";

const PerformanceIndicatorCalculator = ({ rows, results, onChange, onReset }: PerformanceIndicatorCalculatorProps) => {
  const updateValue = (kind: PerformanceInputRow["kind"], field: PerformanceNumberField, value: number | null) => {
    onChange(rows.map((row) => (row.kind === kind ? { ...row, [field]: value } : row)));
  };

  const resultByKind = new Map(results.map((result) => [result.kind, result]));

  return (
    <section data-test="performance-indicator-calculator" className="border-t-2 border-gray-900 px-5 pb-8 pt-3">
      <h2 className="text-center text-xl font-bold text-gray-950 dark:text-white">Performance Indicator</h2>

      <div className="mx-auto mt-10 max-w-[1120px] overflow-x-auto">
        <div className="grid min-w-[900px] grid-cols-[120px_repeat(4,minmax(100px,1fr))_220px] gap-x-4 gap-y-2 items-end">
          <button
            data-test="performance-reset-button"
            type="button"
            onClick={onReset}
            className="mb-1 border border-gray-900 bg-[#D43D4B] px-5 py-2 text-xs font-semibold text-white shadow"
          >
            Reset
          </button>
          <div className="col-span-2 text-center text-base font-medium">Expected Performance</div>
          <div className="col-span-2 text-center text-base font-medium">CP Description</div>
          <div className="text-center text-base font-medium">Performance Indicator</div>

          <div />
          <div className="text-center text-xs font-medium">Speed</div>
          <div className="text-center text-xs font-medium">Consumption</div>
          <div className="text-center text-xs font-medium">Speed</div>
          <div className="text-center text-xs font-medium">Consumption</div>
          <div />

          {rows.map((row) => {
            const result = resultByKind.get(row.kind);
            const performanceLabel = result?.label || result?.status || "(Blank)";
            const usesDarkText = ["#E66C37", "#D9B300", "#88E1B1"].includes(result?.color ?? "");

            return (
              <div key={row.kind} className="contents">
                <div className="self-center text-sm font-medium">{PERFORMANCE_ROW_LABELS[row.kind]}</div>
                <CalculatorNumberInput label="" value={row.expectedSpeed} dataTest={`performance-${row.kind}-expected-speed`} onChange={(value) => updateValue(row.kind, "expectedSpeed", value)} />
                <CalculatorNumberInput label="" value={row.expectedConsumption} dataTest={`performance-${row.kind}-expected-consumption`} onChange={(value) => updateValue(row.kind, "expectedConsumption", value)} />
                <CalculatorNumberInput label="" value={row.cpSpeed} dataTest={`performance-${row.kind}-cp-speed`} onChange={(value) => updateValue(row.kind, "cpSpeed", value)} />
                <CalculatorNumberInput label="" value={row.cpConsumption} dataTest={`performance-${row.kind}-cp-consumption`} onChange={(value) => updateValue(row.kind, "cpConsumption", value)} />
                <div
                  data-test={`performance-${row.kind}-result`}
                  className="self-center rounded-sm px-2 py-1 text-center text-sm font-medium"
                  style={{
                    backgroundColor: result?.color || undefined,
                    color: result?.color ? (usesDarkText ? "#111827" : "#ffffff") : undefined,
                  }}
                >
                  {performanceLabel}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="mx-auto mt-5 max-w-[900px] text-center text-xs text-gray-500 dark:text-gray-400">
        Performance thresholds are provisional until the remote semantic-model decision formula is exported.
      </p>
    </section>
  );
};

export default PerformanceIndicatorCalculator;
